// src/context/CartContext.js
import {
  createContext,
  useContext,
  useEffect,
  useState
} from "react";
import removeSound from "../assets/Remove-Item-From-Cart.wav";
import axios from "../utils/axiosHandler";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const auth = useAuth();
  const user = auth?.user;

  // Initialize cart from appropriate source
  useEffect(() => {
    const initializeCart = async () => {
      if (user) {
        await fetchServerCart();
      } else {
        const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
        setCart(localCart);
      }
    };
    initializeCart();
  }, [user]);

  const fetchServerCart = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Get current server cart
      const { data } = await axios.get("/api/cart", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Process cart items to convert image paths to full URLs if needed
      const processedItems =
        data.items?.map((item) => {
          // Process customization
          if (item.customization) {
            const customization = { ...item.customization };

            // Add URL for preview if it's a path
            if (
              customization.preview &&
              typeof customization.preview === "string"
            ) {
              if (customization.preview.startsWith("/")) {
                customization.previewUrl = `/api${customization.preview}`;
              }
              if (
                customization.previewThumbnail &&
                customization.previewThumbnail.startsWith("/")
              ) {
                customization.previewThumbnailUrl = `/api${customization.previewThumbnail}`;
              }
            }

            // Process custom fields
            if (Array.isArray(customization.customFields)) {
              customization.customFields = customization.customFields.map(
                (field) => {
                  const updatedField = { ...field };

                  // Add URL for image fields
                  if (
                    (field.type === "image" || field.type === "logo") &&
                    field.imageUrl
                  ) {
                    if (field.imageUrl.startsWith("/")) {
                      updatedField.imageUrlFull = `/api${field.imageUrl}`;
                    }
                    if (
                      field.imageThumbnail &&
                      field.imageThumbnail.startsWith("/")
                    ) {
                      updatedField.imageThumbnailUrl = `/api${field.imageThumbnail}`;
                    }
                  }

                  return updatedField;
                }
              );
            }

            // Process required fields
            if (Array.isArray(customization.requiredFields)) {
              customization.requiredFields = customization.requiredFields.map(
                (field) => {
                  const updatedField = { ...field };

                  // Add URL for image fields
                  if (
                    (field.type === "image" || field.type === "logo") &&
                    field.imageUrl
                  ) {
                    if (field.imageUrl.startsWith("/")) {
                      updatedField.imageUrlFull = `/api${field.imageUrl}`;
                    }
                    if (
                      field.imageThumbnail &&
                      field.imageThumbnail.startsWith("/")
                    ) {
                      updatedField.imageThumbnailUrl = `/api${field.imageThumbnail}`;
                    }
                  }

                  return updatedField;
                }
              );
            }

            return { ...item, customization };
          }

          return item;
        }) || [];

      // Handle local cart merging - THIS WAS MISSING!
      const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
      if (localCart.length > 0) {
        // Flag to prevent multiple merges
        const mergeAlreadyAttempted =
          localStorage.getItem("cartMergeAttempted") === "true";

        if (!mergeAlreadyAttempted) {
          // Set flag to prevent duplicate merges
          localStorage.setItem("cartMergeAttempted", "true");

          console.log("Merging", localCart.length, "items from local cart");

          // Process one item at a time with a small delay to prevent race conditions
          for (const item of localCart) {
            try {
              const customizationData = item.customization
                ? {
                  template: item.customization.template || null,
                  preview: item.customization.preview || null,
                  description: item.customization.description || "",
                  customFields: await processCustomizationFields(
                    item.customization.customFields || []
                  ),
                  requiredFields: await processRequiredFields(
                    item.customization.requiredFields || []
                  ),
                }
                : null;

              await axios.post(
                "/api/cart/add",
                {
                  product: {
                    _id: item.product._id,
                  },
                  quantity: item.quantity || 1,
                  customization: customizationData,
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              // Add a small delay between requests
              await new Promise((resolve) => setTimeout(resolve, 300));
            } catch (error) {
              console.error("Error adding item to server cart:", error);
            }
          }

          // Clear local cart
          localStorage.setItem("localCart", "[]");

          // Get updated cart
          const { data: updatedCart } = await axios.get("/api/cart", {
            headers: { Authorization: `Bearer ${token}` },
          });

          setCart(updatedCart.items || []);
          return;
        }
      }

      // If we didn't process local cart, just use server cart
      setCart(processedItems);
    } catch (error) {
      console.error("Error fetching cart:", error);
      setCart([]);
    } finally {
      // Clear the merge flag after a reasonable timeout
      setTimeout(() => {
        localStorage.removeItem("cartMergeAttempted");
      }, 10000);
    }
  };
  const convertFileToBase64 = (file) => {
    // If file is already a string (path or base64), return it as is
    if (typeof file === "string") {
      return Promise.resolve(file);
    }

    // If it's a File object, convert to base64
    if (file instanceof File) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    // If neither string nor File, resolve with original value
    return Promise.resolve(file);
  };

  const processCustomizationFields = async (fields) => {
    if (!fields) return [];
    return Promise.all(
      fields.map(async (field) => {
        if (field.type === "image" || field.type === "logo") {
          // If content is a path from the server, keep it as is
          if (
            typeof field.content === "string" &&
            (field.content.startsWith("/api/upload/") ||
              field.content.startsWith("/upload/"))
          ) {
            return field;
          }

          // If not already a path and not base64, but a string, keep as is
          if (
            typeof field.content === "string" &&
            !field.content.startsWith("data:")
          ) {
            return field;
          }

          // If content is a File object, convert to base64
          if (field.content instanceof File) {
            const base64 = await convertFileToBase64(field.content);
            return { ...field, content: base64 };
          }
        }
        return field;
      })
    );
  };

  const processRequiredFields = async (fields) => {
    if (!fields) return [];
    return Promise.all(
      fields.map(async (field) => {
        if (field.type === "image" || field.type === "logo") {
          // If value is a path from the server, keep it as is
          if (
            typeof field.value === "string" &&
            (field.value.startsWith("/api/upload/") ||
              field.value.startsWith("/upload/"))
          ) {
            return field;
          }

          // If not already a path and not base64, but a string, keep as is
          if (
            typeof field.value === "string" &&
            !field.value.startsWith("data:")
          ) {
            return field;
          }

          // If value is a File object, convert to base64
          if (field.value instanceof File) {
            const base64 = await convertFileToBase64(field.value);
            return { ...field, value: base64 };
          }
        }
        return field;
      })
    );
  };

  const addToCart = async (item) => {
    try {
      if (!item.product.inStock) {
        throw new Error(`${item.product.name} is currently out of stock`);
      }

      if (item.quantity < (item.product.minimumOrder || 1)) {
        throw new Error(
          `Minimum order quantity for ${item.product.name} is ${item.product.minimumOrder}`
        );
      }

      if (user) {
        // Logged in user - add to server cart
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Please log in to add items to cart");
        }

        const customizationData = item.customization
          ? {
            template: item.customization.template || null,
            preview: item.customization.preview || null,
            description: item.customization.description || "",
            customFields: await processCustomizationFields(
              item.customization.customFields || []
            ),
            requiredFields: await processRequiredFields(
              item.customization.requiredFields || []
            ),
          }
          : null;

        const cartItem = {
          product: {
            _id: item.product._id,
          },
          quantity: item.quantity || 1,
          customization: customizationData,
        };

        await axios.post("/api/cart/add", cartItem, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        await fetchServerCart();
      } else {
        // Not logged in - add to local storage cart
        const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
        localCart.push(item);
        localStorage.setItem("localCart", JSON.stringify(localCart));
        setCart(localCart);
      }
    } catch (error) {
      if (error.message && error.message.includes("QuotaExceededError")) {
        throw new Error("Please log in for adding more items to your cart.");
      } else {
        console.error("Error adding to cart:", error);
        throw error;
      }
    }
  };

  const updateCartItem = async (index, updates) => {
    try {
      if (user) {
        // Update server cart
        const token = localStorage.getItem("token");
        if (!token) return;

        const item = cart[index];

        if (updates.quantity) {
          if (!item.product.inStock) {
            throw new Error(`${item.product.name} is currently out of stock`);
          }

          if (updates.quantity < (item.product.minimumOrder || 1)) {
            throw new Error(
              `Minimum order quantity is ${item.product.minimumOrder}`
            );
          }
        }

        let processedUpdates = { ...updates };
        if (updates.customization) {
          processedUpdates.customization = {
            ...updates.customization,
            customFields: await processCustomizationFields(
              updates.customization.customFields
            ),
            requiredFields: await processRequiredFields(
              updates.customization.requiredFields
            ),
          };
        }

        await axios.put(`/api/cart/${index}`, processedUpdates, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        await fetchServerCart();
      } else {
        // Update local storage cart
        const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
        localCart[index] = { ...localCart[index], ...updates };
        localStorage.setItem("localCart", JSON.stringify(localCart));
        setCart(localCart);
      }
    } catch (error) {
      console.error("Error updating cart item:", error);
      throw error;
    }
  };

  const removeFromCart = async (index) => {
    try {
      if (user) {
        // Remove from server cart
        const token = localStorage.getItem("token");
        if (!token) return;

        await axios.delete(`/api/cart/${index}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const sound = new Audio(removeSound);
        sound
          .play()
          .catch((error) =>
            console.warn("Error playing removal sound:", error)
          );

        await fetchServerCart();
      } else {
        // Remove from local storage cart
        const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
        localCart.splice(index, 1);
        localStorage.setItem("localCart", JSON.stringify(localCart));
        setCart(localCart);
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      if (user) {
        // Clear server cart
        const token = localStorage.getItem("token");
        if (!token) return;

        await axios.delete("/api/cart", {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCart([]);
      } else {
        // Clear local storage cart
        localStorage.setItem("localCart", "[]");
        setCart([]);
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      throw error;
    }
  };

  const getCustomizationFile = async (orderId, fieldId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;

      // First check if it's using the new file system (path-based)
      const response = await axios.get(
        `/api/orders/${orderId}/customization/${fieldId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error getting customization file:", error);
      throw error;
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        updateCartItem,
        getCustomizationFile,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);

// // old with img string
// // src/context/CartContext.js
// import React, {
//   createContext,
//   useState,
//   useContext,
//   useEffect,
//   useCallback,
// } from "react";
// import { useAuth } from "./AuthContext";
// import removeSound from "../assets/Remove-Item-From-Cart.wav";

// const CartContext = createContext(null);

// export const CartProvider = ({ children }) => {
//   const [cart, setCart] = useState([]);
//   const auth = useAuth();
//   const user = auth?.user;

//   // Initialize cart from appropriate source
//   useEffect(() => {
//     const initializeCart = async () => {
//       if (user) {
//         await fetchServerCart();
//       } else {
//         const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
//         setCart(localCart);
//       }
//     };
//     initializeCart();
//   }, [user]);

//   const fetchServerCart = async () => {
//     try {
//       const token = localStorage.getItem("token");
//       if (!token) return;

//       // Get current server cart
//       const { data } = await axios.get("/api/cart", {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       // Handle local cart merging
//       const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
//       if (localCart.length > 0) {
//         // Flag to prevent multiple merges
//         const mergeAlreadyAttempted =
//           localStorage.getItem("cartMergeAttempted") === "true";

//         if (!mergeAlreadyAttempted) {
//           // Set flag to prevent duplicate merges
//           localStorage.setItem("cartMergeAttempted", "true");

//           console.log("Merging", localCart.length, "items from local cart");

//           // Process one item at a time with a small delay to prevent race conditions
//           for (const item of localCart) {
//             try {
//               const customizationData = item.customization
//                 ? {
//                     template: item.customization.template || null,
//                     preview: item.customization.preview || null,
//                     description: item.customization.description || "",
//                     customFields: await processCustomizationFields(
//                       item.customization.customFields || []
//                     ),
//                     requiredFields: await processRequiredFields(
//                       item.customization.requiredFields || []
//                     ),
//                   }
//                 : null;

//               await axios.post(
//                 "/api/cart/add",
//                 {
//                   product: {
//                     _id: item.product._id,
//                   },
//                   quantity: item.quantity || 1,
//                   customization: customizationData,
//                 },
//                 {
//                   headers: {
//                     Authorization: `Bearer ${token}`,
//                     "Content-Type": "application/json",
//                   },
//                 }
//               );

//               // Add a small delay between requests
//               await new Promise((resolve) => setTimeout(resolve, 300));
//             } catch (error) {
//               console.error("Error adding item to server cart:", error);
//             }
//           }

//           // Clear local cart
//           localStorage.setItem("localCart", "[]");

//           // Get updated cart
//           const { data: updatedCart } = await axios.get("/api/cart", {
//             headers: { Authorization: `Bearer ${token}` },
//           });

//           setCart(updatedCart.items || []);
//           return;
//         }
//       }

//       // If we didn't process local cart, just use server cart
//       setCart(data.items || []);
//     } catch (error) {
//       console.error("Error fetching cart:", error);
//       setCart([]);
//     } finally {
//       // Clear the merge flag after a reasonable timeout
//       setTimeout(() => {
//         localStorage.removeItem("cartMergeAttempted");
//       }, 10000);
//     }
//   };

//   const convertFileToBase64 = (file) => {
//     return new Promise((resolve, reject) => {
//       const reader = new FileReader();
//       reader.onload = () => resolve(reader.result);
//       reader.onerror = reject;
//       reader.readAsDataURL(file);
//     });
//   };

//   const processCustomizationFields = async (fields) => {
//     if (!fields) return [];
//     return Promise.all(
//       fields.map(async (field) => {
//         if (field.type === "image" || field.type === "logo") {
//           if (
//             typeof field.content === "string" &&
//             !field.content.startsWith("data:")
//           ) {
//             return field;
//           }
//           if (field.content instanceof File) {
//             const base64 = await convertFileToBase64(field.content);
//             return { ...field, content: base64 };
//           }
//         }
//         return field;
//       })
//     );
//   };

//   const processRequiredFields = async (fields) => {
//     if (!fields) return [];
//     return Promise.all(
//       fields.map(async (field) => {
//         if (field.type === "image" || field.type === "logo") {
//           if (
//             typeof field.value === "string" &&
//             !field.value.startsWith("data:")
//           ) {
//             return field;
//           }
//           if (field.value instanceof File) {
//             const base64 = await convertFileToBase64(field.value);
//             return { ...field, value: base64 };
//           }
//         }
//         return field;
//       })
//     );
//   };

//   const addToCart = async (item) => {
//     try {
//       if (!item.product.inStock) {
//         throw new Error(`${item.product.name} is currently out of stock`);
//       }

//       if (item.quantity < (item.product.minimumOrder || 1)) {
//         throw new Error(
//           `Minimum order quantity for ${item.product.name} is ${item.product.minimumOrder}`
//         );
//       }

//       if (user) {
//         // Logged in user - add to server cart
//         const token = localStorage.getItem("token");
//         if (!token) {
//           throw new Error("Please log in to add items to cart");
//         }

//         const customizationData = item.customization
//           ? {
//               template: item.customization.template || null,
//               preview: item.customization.preview || null,
//               description: item.customization.description || "",
//               customFields: await processCustomizationFields(
//                 item.customization.customFields || []
//               ),
//               requiredFields: await processRequiredFields(
//                 item.customization.requiredFields || []
//               ),
//             }
//           : null;

//         const cartItem = {
//           product: {
//             _id: item.product._id,
//           },
//           quantity: item.quantity || 1,
//           customization: customizationData,
//         };

//         await axios.post("/api/cart/add", cartItem, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         });

//         await fetchServerCart();
//       } else {
//         // Not logged in - add to local storage cart
//         const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
//         localCart.push(item);
//         localStorage.setItem("localCart", JSON.stringify(localCart));
//         setCart(localCart);
//       }
//     } catch (error) {
//       if (error.message && error.message.includes("QuotaExceededError")) {
//         throw new Error("Please log in for adding more items to your cart.");
//       } else {
//         console.error("Error adding to cart:", error);
//         throw error;
//       }
//     }
//   };

//   const updateCartItem = async (index, updates) => {
//     try {
//       if (user) {
//         // Update server cart
//         const token = localStorage.getItem("token");
//         if (!token) return;

//         const item = cart[index];

//         if (updates.quantity) {
//           if (!item.product.inStock) {
//             throw new Error(`${item.product.name} is currently out of stock`);
//           }

//           if (updates.quantity < (item.product.minimumOrder || 1)) {
//             throw new Error(
//               `Minimum order quantity is ${item.product.minimumOrder}`
//             );
//           }
//         }

//         let processedUpdates = { ...updates };
//         if (updates.customization) {
//           processedUpdates.customization = {
//             ...updates.customization,
//             customFields: await processCustomizationFields(
//               updates.customization.customFields
//             ),
//             requiredFields: await processRequiredFields(
//               updates.customization.requiredFields
//             ),
//           };
//         }

//         await axios.put(`/api/cart/${index}`, processedUpdates, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         });

//         await fetchServerCart();
//       } else {
//         // Update local storage cart
//         const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
//         localCart[index] = { ...localCart[index], ...updates };
//         localStorage.setItem("localCart", JSON.stringify(localCart));
//         setCart(localCart);
//       }
//     } catch (error) {
//       console.error("Error updating cart item:", error);
//       throw error;
//     }
//   };

//   const removeFromCart = async (index) => {
//     try {
//       if (user) {
//         // Remove from server cart
//         const token = localStorage.getItem("token");
//         if (!token) return;

//         await axios.delete(`/api/cart/${index}`, {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const sound = new Audio(removeSound);
//         sound
//           .play()
//           .catch((error) =>
//             console.warn("Error playing removal sound:", error)
//           );

//         await fetchServerCart();
//       } else {
//         // Remove from local storage cart
//         const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
//         localCart.splice(index, 1);
//         localStorage.setItem("localCart", JSON.stringify(localCart));
//         setCart(localCart);
//       }
//     } catch (error) {
//       console.error("Error removing from cart:", error);
//       throw error;
//     }
//   };

//   const clearCart = async () => {
//     try {
//       if (user) {
//         // Clear server cart
//         const token = localStorage.getItem("token");
//         if (!token) return;

//         await axios.delete("/api/cart", {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         setCart([]);
//       } else {
//         // Clear local storage cart
//         localStorage.setItem("localCart", "[]");
//         setCart([]);
//       }
//     } catch (error) {
//       console.error("Error clearing cart:", error);
//       throw error;
//     }
//   };

//   const getCustomizationFile = async (orderId, fieldId) => {
//     try {
//       const token = localStorage.getItem("token");
//       if (!token) return null;

//       const response = await axios.get(
//         `/api/orders/${orderId}/customization/${fieldId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//           responseType: "blob",
//         }
//       );

//       return response.data;
//     } catch (error) {
//       console.error("Error getting customization file:", error);
//       throw error;
//     }
//   };

//   return (
//     <CartContext.Provider
//       value={{
//         cart,
//         addToCart,
//         removeFromCart,
//         clearCart,
//         updateCartItem,
//         getCustomizationFile,
//       }}
//     >
//       {children}
//     </CartContext.Provider>
//   );
// };

// export const useCart = () => useContext(CartContext);
